import { useEffect, useState } from 'react';
import { useToast } from '../components/Toast';
import { useModal } from '../components/ModalProvider';
import { fmtDate, fmtDateTime } from '../utils/format';
import { listCskhWeekReports, getCskhWeekReportById } from '../api/cskhLogs';
import { salesHeads } from '../api/users';
import { createDirectTask } from '../api/tasks';

// Các mục cần theo dõi (hiển thị bảng: nội dung cũ · note TP · chốt nội dung).
const FOLLOW_SECTIONS = [
  { key: 'unhappy', label: 'KH không hài lòng', color: '#dc2626', bg: 'rgba(220,38,38,.08)' },
  { key: 'stopped', label: 'KH dừng giảm', color: '#9333ea', bg: 'rgba(147,51,234,.10)' },
  { key: 'xlkn', label: 'Xử lý khiếu nại (XLKN)', color: 'var(--accent2)', bg: 'rgba(91,140,255,.10)' },
];

export default function CskhReportPage() {
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null); // báo cáo đang xem (full)
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    listCskhWeekReports()
      .then((d) => {
        setList(d);
        if (d.length) getCskhWeekReportById(d[0]._id).then(setActive).catch(() => {});
      })
      .catch((err) => {
        if (err.response?.status === 403) setDenied(true);
      })
      .finally(() => setLoading(false));
  }, []);

  function openTaskForm() {
    openModal(<DirectTaskForm active={active} onClose={closeModal} toast={toast} />);
  }

  if (denied)
    return (
      <>
        <Head />
        <div className="card">
          <div className="empty" style={{ padding: 40, textAlign: 'center' }}>
            🔒 Chỉ <b>Phòng CSKH</b> và <b>Giám đốc</b> xem được.
          </div>
        </div>
      </>
    );

  return (
    <>
      <Head />
      <div className="card">
        <div className="row between" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>📋 Báo cáo CSKH tuần đã nộp</h3>
          <button className="btn sm" onClick={openTaskForm}>
            ＋ Tạo công việc
          </button>
        </div>

        {loading ? (
          <div className="empty">Đang tải…</div>
        ) : !list.length ? (
          <div className="empty">Chưa có báo cáo tuần nào được nộp.</div>
        ) : (
          <>
            {/* Timeline các tuần đã nộp */}
            <div className="tl-wrap">
              <button className="tl-arrow" onClick={() => document.getElementById('csr-tl')?.scrollBy({ left: -400, behavior: 'smooth' })}>‹</button>
              <div className="tl-scroll" id="csr-tl">
                {[...list].reverse().map((r) => (
                  <div
                    key={r._id}
                    className={'cslog-cell' + (active && String(active._id) === String(r._id) ? ' thu' : '')}
                    style={{ cursor: 'pointer' }}
                    onClick={() => getCskhWeekReportById(r._id).then(setActive)}
                  >
                    <div className="cslog-head">{r.label || `${fmtDate(r.from)} → ${fmtDate(r.to)}`}</div>
                    {r.user?.name && <div className="small muted" style={{ textAlign: 'center', marginBottom: 4 }}>{r.user.name}</div>}
                    <div className="cslog-row"><span>👥 Đã LH</span><b>{r.totalContacted}</b></div>
                    <div className="cslog-row"><span>🟢</span><b>{r.counts?.happy || 0}</b></div>
                    <div className="cslog-row"><span>🔴</span><b>{r.counts?.unhappy || 0}</b></div>
                    <div className="cslog-row"><span>⚠️ XLKN</span><b>{r.counts?.xlkn || 0}</b></div>
                  </div>
                ))}
              </div>
              <button className="tl-arrow" onClick={() => document.getElementById('csr-tl')?.scrollBy({ left: 400, behavior: 'smooth' })}>›</button>
            </div>

            {/* Chi tiết báo cáo đang chọn */}
            {active && (
              <div style={{ marginTop: 16 }}>
                <div className="small muted" style={{ marginBottom: 12 }}>
                  📌 {active.label} · {active.user?.name} · nộp {fmtDateTime(active.submittedAt)}
                </div>

                {/* 3 thẻ tổng quan */}
                {(() => {
                  const total = active.totalContacted || 0;
                  const happyN = (active.happy || []).length;
                  const followN = (active.unhappy || []).length + (active.stopped || []).length + (active.xlkn || []).length;
                  const pct = total ? Math.round((happyN / total) * 100) : 0;
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, marginBottom: 18 }}>
                      <div className="card" style={{ padding: '12px 16px' }}>
                        <div className="small muted">Tổng KH đã liên hệ</div>
                        <div><b style={{ fontSize: 26 }}>{total}</b></div>
                      </div>
                      <div className="card" style={{ padding: '12px 16px', background: 'rgba(22,163,74,.08)', borderColor: 'rgba(22,163,74,.3)' }}>
                        <div className="small" style={{ color: '#16a34a', fontWeight: 700 }}>● KH hài lòng</div>
                        <div><b style={{ fontSize: 26, color: '#16a34a' }}>{happyN}</b> <span className="small muted">{pct}%</span></div>
                      </div>
                      <div className="card" style={{ padding: '12px 16px' }}>
                        <div className="small muted">Cần theo dõi tiếp</div>
                        <div><b style={{ fontSize: 26, color: 'var(--warn)' }}>{followN}</b> <span className="small muted">cần theo dõi</span></div>
                      </div>
                    </div>
                  );
                })()}

                {/* 3 bảng cần theo dõi */}
                {FOLLOW_SECTIONS.map((s) => (
                  <div key={s.key} style={{ marginBottom: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderLeft: `3px solid ${s.color}`, background: s.bg, borderRadius: '0 6px 6px 0', marginBottom: 6 }}>
                      <b style={{ color: s.color }}>{s.label}</b>
                      <span className="small muted">({(active[s.key] || []).length})</span>
                    </div>
                    {(active[s.key] || []).length ? (
                      <div className="tbl-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th style={{ width: 140 }}>Mã / Tên KH</th>
                              <th style={{ width: '22%' }}>Nội dung cũ</th>
                              <th style={{ width: '22%' }}>Giải trình nhân viên</th>
                              <th style={{ width: '22%' }}>Note trưởng nhóm</th>
                              <th>Chốt nội dung</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(active[s.key] || []).map((it, i) => (
                              <tr key={i}>
                                <td style={{ verticalAlign: 'top' }}>
                                  <code style={{ color: 'var(--warn)' }}>{it.custCode || '—'}</code>
                                  <div className="small muted">{it.custName || '—'}</div>
                                </td>
                                <td style={{ verticalAlign: 'top' }}>{it.content || <span className="small muted">—</span>}</td>
                                <td style={{ verticalAlign: 'top' }}>
                                  {it.staffNote ? <span style={{ color: 'var(--ok)' }}>{it.staffNote}</span> : <span className="small muted">—</span>}
                                </td>
                                <td style={{ verticalAlign: 'top' }}>
                                  {it.leaderNote ? <span style={{ color: 'var(--accent2)' }}>{it.leaderNote}</span> : <span className="small muted">—</span>}
                                </td>
                                <td style={{ verticalAlign: 'top' }}>{it.chot || <span className="small muted">—</span>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="small muted" style={{ padding: '2px 12px' }}>Không có.</div>
                    )}
                  </div>
                ))}

                {active.weekNote && (
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 6 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>Nhận xét chung của CSKH trong tuần</div>
                    <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{active.weekNote}</div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

// Form tạo công việc giao cho 1 trưởng phòng nhóm kinh doanh.
function DirectTaskForm({ active, onClose, toast }) {
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const dlDefault = new Date(Date.now() + 3 * 864e5).toISOString().slice(0, 10);
  const [deadline, setDeadline] = useState(dlDefault);
  const [assignTo, setAssignTo] = useState('');
  const [heads, setHeads] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    salesHeads().then(setHeads).catch(() => {});
  }, []);

  async function submit() {
    if (!title.trim() || !assignTo) return;
    setBusy(true);
    try {
      const r = await createDirectTask({ title: title.trim(), detail: detail.trim(), deadline, assignTo });
      toast(r.message || 'Đã giao công việc');
      onClose();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi', 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <span className="close" onClick={onClose}>×</span>
      <h3>Tạo công việc cho Trưởng phòng KD</h3>
      <div className="field">
        <label>Tiêu đề</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="vd: Liên hệ lại nhóm KH không hài lòng" />
      </div>
      <div className="field">
        <label>Nội dung</label>
        <textarea rows={3} value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Mô tả công việc…" />
      </div>
      <div className="two-col">
        <div className="field">
          <label>Người nhận (Trưởng phòng nhóm KD)</label>
          <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
            <option value="">— Chọn trưởng phòng —</option>
            {heads.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name} {h.group ? `· ${h.group}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Hạn hoàn thành</label>
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
        </div>
      </div>
      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer" />
        <button className="btn ghost" onClick={onClose}>Hủy</button>
        <button className="btn" disabled={busy || !title.trim() || !assignTo} onClick={submit}>
          {busy ? 'Đang giao…' : 'Giao việc'}
        </button>
      </div>
    </>
  );
}

function Head() {
  return (
    <div className="page-head">
      <div>
        <h2>Báo cáo CSKH</h2>
        <div className="sub">Báo cáo tuần do nhân viên CSKH nộp</div>
      </div>
    </div>
  );
}
